/** @jsxImportSource @emotion/react */
import React, { FC, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { ILocation } from '../../../../api/locations/types';
import lightning from '../../../../assets/icons/lightning.svg';
import lightningStroke from '../../../../assets/icons/lightning-stroke.svg';
import charging from '../../../../assets/icons/charging.svg';
import { EStationPricingOption } from '../../../../api/types';
import { IconArrowRight } from '../../../../assets/icons/icon-arrow-right';
import { EStationStatus, IStation, IStationConnector } from '../../../../api/station/types';
import Badge, { EBadgeType } from '../../../../components/badge/badge.component';

import locationItemStyles from './location-item.styles';
import { getDistanceFromMeters } from '../../../../utils/utils';

type PropTypes = {
    location: ILocation;
    onClick?: () => void;
    isMiles?: boolean;
};

export const LocationItem: FC<PropTypes> = ({ location, onClick, isMiles = false }) => {
    const intl = useIntl();
    const businessTitle = useMemo(() => {
        return `${location.address.province ?? 'CA'}*${location.name ?? 'GRN'}`;
    }, [location.address.province, location.name]);

    const distance = useMemo(() => {
        return getDistanceFromMeters(location.distance, isMiles, intl);
    }, [location.distance, isMiles, intl]);

    const renderFreeBadge = useCallback(() => {
        const isFree =
            (location?.stations ?? [])?.some(station =>
                station.status === EStationStatus.AVAILABLE &&
                station.connectors?.some(connector => connector.tariff?.pricingOption === EStationPricingOption.free)
            ) ?? false;

        if (isFree) {
            return <Badge type={EBadgeType.FREE} />;
        }

        return null;
    }, [location?.stations]);

    const allConnectors = useMemo(() => {
        return (
            (location?.stations ?? [])?.reduce(
                (accumulator: (IStationConnector & { station: IStation })[], station) => {
                    const mappedConnectors = station?.connectors?.map(connector => {
                        return {
                            ...connector,
                            station,
                        };
                    });
                    return [...accumulator, ...mappedConnectors];
                },
                []
            ) ?? []
        );
    }, [location?.stations]);

    //? todo don't mix data and ui ?
    const availableConnectors = useMemo(() => {
        const { total, available } = allConnectors?.reduce(
            (accumulator, connector) => {
                accumulator.total += 1;
                if (
                    connector.status === EStationStatus.AVAILABLE &&
                    connector.station.online &&
                    connector.station.mode !== 'Inactive'
                ) {
                    accumulator.available += 1;
                }

                return accumulator;
            },
            {
                total: 0,
                available: 0,
            }
        );
        return (
            <span>
                <span css={locationItemStyles.availableConnectors}>{available}</span>/{total}
            </span>
        );
    }, [allConnectors]);

    const getMinMaxPower = useCallback((connectors: IStationConnector[]) => {
        if (connectors.length) {
            const min = connectors?.reduce((prev, curr) =>
                prev.maxPower < curr.maxPower ? prev : curr
            );
            const max = connectors?.reduce((prev, curr) =>
                prev.maxPower > curr.maxPower ? prev : curr
            );

            return {
                minPower: min.maxPower,
                maxPower: max.maxPower,
            };
        } else {
            return {
                minPower: 0,
                maxPower: 0,
            };
        }
    }, []);

    const poweLevel = useMemo(() => {
        const { maxPower } = getMinMaxPower(allConnectors);
        let level = 0;
        if (maxPower > 120) {
            level = 3;
        } else if (maxPower > 11) {
            level = 2;
        } else {
            level = 1;
        }

        return Array.from({ length: 3 }, (_, index) => (
            <img
                key={index}
                src={index < level ? lightning : lightningStroke}
                width={9}
                alt="grizzl-e"
            />
        ));
    }, [allConnectors]);

    const powerBadge = useMemo(() => {
        const power = getMinMaxPower(allConnectors);

        if (power) {
            return <div css={locationItemStyles.powerBadge}>{poweLevel}</div>;
        }

        return null;
    }, [allConnectors, getMinMaxPower, intl]);

    const locationAddress = useMemo(() => {
        const { zip, city, street_1, province } = location.address;
        return `${city}, ${province ?? ''} ${location.name ?? street_1}`;
    }, [location.address]);

    return (
        <div css={locationItemStyles.container} onClick={onClick}>
            <div css={locationItemStyles.infoBlock}>
                <div css={locationItemStyles.infoBlockBadges}>
                    {powerBadge}
                    <div css={locationItemStyles.connectorsBadge}>
                        <img src={charging} width={15} height={15} alt="grizzl-e" />
                        {availableConnectors}
                    </div>
                    {renderFreeBadge()}
                </div>
                <div css={locationItemStyles.title}>{locationAddress}</div>
                <div css={locationItemStyles.subtitle}>
                    {distance} - {businessTitle}
                </div>
            </div>
            <div css={locationItemStyles.infoAction}>
                <div css={locationItemStyles.abbreviation}></div>
                <IconArrowRight size={16} color={'black'} />
            </div>
        </div>
    );
};
